import axiosInstance from 'src/config/axios.config';
import { API_ENDPOINTS } from 'src/config/api.config';

// Interface tanımlamaları
export interface EmployeeTransferDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  branchId: string;
  branchName: string;
  companyId: string;
  companyName: string;
}

export interface PaginatedEmployeeResponse {
  content: EmployeeTransferDto[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      direction: string;
      property: string;
    };
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
}

export interface CompanyDto {
  id: string;
  name: string;
}

export interface BranchDto {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
}

// Employee Transfer API
export const employeeTransferApi = {
  // Çalışanları getir (sayfalama ile)
  async getMyEmployees(
    page = 0,
    size = 50,
    sortBy = 'firstName',
    sortDirection = 'asc'
  ): Promise<PaginatedEmployeeResponse> {
    const response = await axiosInstance.get<PaginatedEmployeeResponse>(
      `${API_ENDPOINTS.EMPLOYEES}/my-employees`,
      {
        params: {
          page,
          size,
          sortBy,
          sortDirection,
        },
      }
    );
    return response.data;
  },

  // Şube ID'si ile çalışanları getir (Backend filtreleme)
  async getEmployeesByBranch(
    branchId: string,
    page = 0,
    size = 50,
    sortBy = 'firstName',
    sortDirection = 'asc'
  ): Promise<PaginatedEmployeeResponse> {
    const response = await axiosInstance.get<PaginatedEmployeeResponse>(
      `${API_ENDPOINTS.EMPLOYEES}/my-employees`,
      {
        params: {
          page,
          size,
          sortBy,
          sortDirection,
          branchId, // Backend filtreleme parametresi
        },
      }
    );
    return response.data;
  },

  // Branch details için özel endpoint - sadece o şubedeki çalışanları getir
  async getEmployeesForBranchTransfer(
    branchId: string,
    page = 0,
    size = 50,
    sortBy = 'firstName',
    sortDirection = 'asc'
  ): Promise<PaginatedEmployeeResponse> {
    const response = await axiosInstance.get<PaginatedEmployeeResponse>(
      `${API_ENDPOINTS.EMPLOYEES}/by-branch/${branchId}`,
      {
        params: {
          page,
          size,
          sortBy,
          sortDirection,
        },
      }
    );
    return response.data;
  },

  // Şirketleri getir
  async getCompanies(): Promise<CompanyDto[]> {
    const response = await axiosInstance.get<CompanyDto[]>(
      `${API_ENDPOINTS.COMPANIES}/user-creation/companies`
    );
    return response.data;
  },

  // Şirketin şubelerini getir
  async getBranchesByCompany(companyId: string): Promise<BranchDto[]> {
    const response = await axiosInstance.get<BranchDto[]>(
      `${API_ENDPOINTS.COMPANIES}/${companyId}/branches/user-creation`
    );
    return response.data;
  },

  // Çalışan transferi yap
  async transferEmployee(employeeId: string, newBranchId: string): Promise<void> {
    await axiosInstance.put(
      `${API_ENDPOINTS.EMPLOYEES}/${employeeId}/transfer/${newBranchId}`
    );
  },

  // Toplu çalışan transferi (backend'de tek endpoint olmadığı için Promise.allSettled kullan)
  async bulkTransferEmployees(
    employeeIds: string[],
    newBranchId: string
  ): Promise<{ success: string[]; failed: { id: string; error: string }[] }> {
    const transferPromises = employeeIds.map(async (employeeId) => ({
      employeeId,
      result: await this.transferEmployee(employeeId, newBranchId).then(
        () => ({ success: true }),
        (error: any) => ({ 
          success: false, 
          error: error.response?.data?.message || error.message || 'Transfer başarısız' 
        })
      )
    }));

    const transferResults = await Promise.allSettled(transferPromises);
    
    const results = {
      success: [] as string[],
      failed: [] as { id: string; error: string }[],
    };

    transferResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        const { employeeId, result: transferResult } = result.value;
        if (transferResult.success) {
          results.success.push(employeeId);
        } else {
          results.failed.push({
            id: employeeId,
            error: 'error' in transferResult ? transferResult.error : 'Transfer başarısız',
          });
        }
      } else {
        // Promise itself rejected
        results.failed.push({
          id: 'unknown',
          error: 'Promise failed',
        });
      }
    });

    return results;
  },
};